import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  ConsumeOperation,
  generateUniqueId,
  hasExistingUndo,
  isConsumeOperation,
  ProcessorCallbacks,
  QueueOperation,
  UndoOperation,
} from "../types/queue.ts";
import { QueueProcessor } from "../queue/QueueProcessor.ts";
import { provideSuccessFeedback } from "utils/vibration.ts";

const QUEUE_STORAGE_KEY = "unified-operation-queue-v1";

// Storage utilities
function loadQueueFromStorage(): QueueOperation[] {
  try {
    const data = localStorage.getItem(QUEUE_STORAGE_KEY);
    const queue = data ? JSON.parse(data) as QueueOperation[] : [];
    return queue.map((operation) =>
      operation.status === "IN_PROGRESS"
        ? { ...operation, status: "PENDING", errorMessage: undefined }
        : operation
    );
  } catch {
    return [];
  }
}

function saveQueueToStorage(queue: QueueOperation[]): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue.slice(0, 50))); // Keep max 50 items
  } catch (error) {
    console.warn("Failed to save queue to storage:", error);
  }
}

export function useUnifiedQueue() {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<QueueOperation[]>(() =>
    loadQueueFromStorage()
  );
  const queueRef = useRef(queue);
  const processorRef = useRef<QueueProcessor | null>(null);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const getOperationAmount = useCallback(
    (operation: QueueOperation): number => {
      if (operation.type === "CONSUME") {
        return operation.amount ?? 1;
      }

      const targetOperation = queueRef.current.find((
        op,
      ): op is ConsumeOperation =>
        isConsumeOperation(op) && op.id === operation.targetOperationId
      );

      return targetOperation?.amount ?? 1;
    },
    [],
  );

  // React Query cache management functions
  const applyOptimisticUpdate = useCallback((operation: QueueOperation) => {
    queryClient.setQueryData<any[]>(["stock"], (old) => {
      if (!old) return old;

      const operationAmount = getOperationAmount(operation);
      const adjustment = operation.type === "CONSUME"
        ? -operationAmount
        : operationAmount;

      return old.map((item) =>
        item.product_id === operation.productId
          ? { ...item, amount: Math.max(0, item.amount + adjustment) }
          : item
      );
    });
  }, [queryClient, getOperationAmount]);

  const revertOptimisticUpdate = useCallback((operation: QueueOperation) => {
    queryClient.setQueryData<any[]>(["stock"], (old) => {
      if (!old) return old;

      const operationAmount = getOperationAmount(operation);
      const adjustment = operation.type === "CONSUME"
        ? operationAmount
        : -operationAmount; // Opposite of original update

      return old.map((item) =>
        item.product_id === operation.productId
          ? { ...item, amount: Math.max(0, item.amount + adjustment) }
          : item
      );
    });
  }, [queryClient, getOperationAmount]);

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["stock"] });
  }, [queryClient]);

  // Queue management functions
  const updateOperationStatus = useCallback(
    (operationId: string, status: QueueOperation["status"]) => {
      setQueue((prev) =>
        prev.map((op) => op.id === operationId ? { ...op, status } : op)
      );
    },
    [],
  );

  const updateOperation = useCallback(
    (operationId: string, updates: Partial<QueueOperation>) => {
      setQueue((prev) =>
        prev.map((op) =>
          op.id === operationId ? { ...op, ...updates } as QueueOperation : op
        )
      );
    },
    [],
  );

  const removeOperation = useCallback((operationId: string) => {
    setQueue((prev) => prev.filter((op) => op.id !== operationId));
  }, []);

  const removeCompletedPair = useCallback(
    (consumeId: string, undoId: string) => {
      setQueue((prev) =>
        prev.filter((op) => op.id !== consumeId && op.id !== undoId)
      );
    },
    [],
  );

  // Processor callbacks
  const processorCallbacks = useMemo<ProcessorCallbacks>(() => ({
    updateOperationStatus,
    updateOperation,
    onQueueEmpty: refreshData,
    onOperationFailed: revertOptimisticUpdate,
    onOperationCompleted: (operation: QueueOperation) => {
      // Show toast for completed undo operations
      if (operation.type === "UNDO") {
        const stockData = queryClient.getQueryData<any[]>(["stock"]);
        const productName = stockData?.find((item) =>
          item.product_id === operation.productId
        )?.product?.name || `Product ID: ${operation.productId}`;
        toast.success(`Successfully undid consumption of ${productName}`);
        provideSuccessFeedback();
      } else if (operation.type === "CONSUME") {
        // Provide feedback for successful consumption (no toast to avoid spam)
        provideSuccessFeedback();
      }
    },
    removeCompletedPair,
  }), [
    updateOperationStatus,
    updateOperation,
    refreshData,
    revertOptimisticUpdate,
    removeCompletedPair,
    queryClient,
  ]);

  // Initialize processor with callbacks
  useEffect(() => {
    if (!processorRef.current) {
      processorRef.current = new QueueProcessor(processorCallbacks);
    }
  }, [processorCallbacks]);

  // Trigger processing when queue changes
  useEffect(() => {
    if (processorRef.current) {
      processorRef.current.onQueueChange(queue);
    }
  }, [queue]);

  // Persist queue to storage whenever it changes
  useEffect(() => {
    saveQueueToStorage(queue);
  }, [queue]);

  // Public interface functions
  const addOperation = useCallback((operation: QueueOperation) => {
    // Apply optimistic update immediately
    applyOptimisticUpdate(operation);

    // Add to queue for backend processing
    setQueue((prev) => [...prev, operation]);
  }, [applyOptimisticUpdate]);

  const addConsumeOperation = useCallback(
    (productId: number, amount: number = 1) => {
      const normalizedAmount = Number.isFinite(amount) && amount > 0
        ? amount
        : 1;

      const operation: ConsumeOperation = {
        id: generateUniqueId(),
        type: "CONSUME",
        productId,
        amount: normalizedAmount,
        status: "PENDING",
        timestamp: Date.now(),
      };

      addOperation(operation);
      return operation.id;
    },
    [addOperation],
  );

  // Helper to handle undo logic
  const requestUndo = useCallback(() => {
    // Find most recent undoable operation
    const undoableOp = [...queue]
      .reverse()
      .find((op) =>
        isConsumeOperation(op) &&
        !hasExistingUndo(op, queue) && // NEW: Skip operations with existing undos
        (op.status === "PENDING" || op.status === "IN_PROGRESS" ||
          op.status === "COMPLETED")
      ) as ConsumeOperation | undefined;

    if (!undoableOp) return false;

    if (undoableOp.status === "PENDING") {
      // Remove pending operation entirely - no backend call needed
      removeOperation(undoableOp.id);

      // Apply optimistic update (revert the consume)
      queryClient.setQueryData<any[]>(["stock"], (old) => {
        if (!old) return old;
        return old.map((item) =>
          item.product_id === undoableOp.productId
            ? { ...item, amount: item.amount + (undoableOp.amount ?? 1) }
            : item
        );
      });

      // Show toast for pending operation undo
      const stockData = queryClient.getQueryData<any[]>(["stock"]);
      const productName = stockData?.find((item) =>
        item.product_id === undoableOp.productId
      )?.product?.name || `Product ID: ${undoableOp.productId}`;
      toast.success(`Undid consumption of ${productName}`);
      provideSuccessFeedback();
    } else {
      // Create undo operation for in-progress or completed operation
      const undoOperation: UndoOperation = {
        id: generateUniqueId(),
        type: "UNDO",
        targetOperationId: undoableOp.id,
        productId: undoableOp.productId,
        status: "PENDING",
        timestamp: Date.now(),
      };

      addOperation(undoOperation);
    }

    return true;
  }, [queue, queryClient, removeOperation, addOperation]);

  // Clear all failed operations
  const clearFailedOperations = useCallback(() => {
    setQueue((prev) => prev.filter((op) => op.status !== "FAILED"));
  }, []);

  // Clear entire queue (for debugging)
  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  // Cleanup old operations (completed/failed operations older than 5 minutes)
  const cleanupOldOperations = useCallback(() => {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);

    setQueue((prev) =>
      prev.filter((op) => {
        // Keep pending and in-progress operations regardless of age
        if (op.status === "PENDING" || op.status === "IN_PROGRESS") {
          return true;
        }

        // Remove completed or failed operations older than 5 minutes
        if (
          (op.status === "COMPLETED" || op.status === "FAILED") &&
          op.timestamp < fiveMinutesAgo
        ) {
          return false;
        }

        return true;
      })
    );
  }, []);

  // Auto-cleanup every minute
  useEffect(() => {
    const cleanupInterval = setInterval(cleanupOldOperations, 60 * 1000); // Run every minute

    // Also run cleanup immediately on mount
    cleanupOldOperations();

    return () => clearInterval(cleanupInterval);
  }, [cleanupOldOperations]);

  // Get queue statistics
  const queueStats = useMemo(() => ({
    total: queue.filter((op) => op.status !== "COMPLETED").length,
    pending: queue.filter((op) => op.status === "PENDING").length,
    inProgress: queue.filter((op) => op.status === "IN_PROGRESS").length,
    failed: queue.filter((op) => op.status === "FAILED").length,
    completed: queue.filter((op) => op.status === "COMPLETED").length,
  }), [queue]);

  return {
    queue,
    queueStats,
    addOperation,
    addConsumeOperation,
    removeOperation,
    updateOperation,
    requestUndo,
    clearFailedOperations,
    clearQueue,
    cleanupOldOperations,
    hasPendingOperations: queueStats.pending > 0,
    hasFailedOperations: queueStats.failed > 0,
    canUndo: queue.some((op) =>
      isConsumeOperation(op) &&
      !hasExistingUndo(op, queue) &&
      (op.status === "PENDING" || op.status === "IN_PROGRESS" ||
        op.status === "COMPLETED")
    ),
  };
}
